import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useFamilyStore } from '../store/familyStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import NavBar from '../components/NavBar';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { signIn, isLoading, error, clearError } = useAuthStore();
  const { fetchFamily } = useFamilyStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    try {
      await signIn(email, password);
      // Check if user has a family
      await fetchFamily();
      const family = useFamilyStore.getState().family;
      
      // Redirect to family creation if no family exists
      if (family === null) {
        navigate('/family', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      // Check if error is about unverified email
      const errorMessage = err.message || '';
      if (errorMessage.includes('verify your email') || errorMessage.includes('verification code has been sent')) {
        // Redirect to signup page with email pre-filled and show verification screen
        navigate('/signup', { 
          state: { 
            email, 
            showVerification: true,
            message: errorMessage.includes('verification code has been sent') 
              ? 'A new verification code has been sent to your email.'
              : 'Please verify your email address to continue.'
          } 
        });
      }
      // Otherwise, error is handled by the store
    }
  };

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} flex flex-col relative`}>
      <NavBar className={`${tokens.card.bg} shadow-sm`} containerClassName="px-3 sm:px-4">
        <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Runow</h1>
      </NavBar>

      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollable">
        <div className="min-h-full flex items-start sm:items-center justify-center p-4">
          <div className={`max-w-md w-full ${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-6 sm:p-8`}>
        <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-6 text-center`}>
          Runow
        </h1>
        <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-6 text-center`}>
          Sign In
        </h2>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={`w-full px-4 py-3 pr-12 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-sm ${tokens.text.muted} hover:${tokens.text.primary} focus:outline-none transition-colors rounded-sm min-h-[48px] flex items-center justify-center font-medium`}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full ${tokens.button.primary} disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-3 px-4 transition-colors min-h-[48px]`}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 space-y-2 text-center">
          <p className={`text-sm ${tokens.text.muted}`}>
            Don't have an account?{' '}
            <Link to="/signup" className={`${tokens.text.accent} hover:opacity-80 font-medium transition-opacity`}>
              Sign up
            </Link>
          </p>
          <p className="text-sm">
            <Link to="/forgot-password" className={`${tokens.text.accent} hover:opacity-80 font-medium transition-opacity`}>
              Forgot your password?
            </Link>
          </p>
          <p className={`text-xs ${tokens.text.muted} mt-2`}>
            Admin?{' '}
            <Link to="/admin/login" className={`${tokens.text.accent} hover:opacity-80 font-medium transition-opacity`}>
              Admin login
            </Link>
          </p>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}
